import { Button, Checkbox, Flex, Modal, Select } from "antd";
import { Form, Input, Radio, Row, Col, Divider, Tooltip } from "antd";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState, useRecoilState } from "recoil";
import "./index.scss";
import {
  InvoiceTypeMap,
  PayResultStatus,
  SOURCE_ORDER_TYPE,
  PodOrderStatus,
  OrderTypeToInvoiceContentMap,
  podVersionNameMap,
} from "@/utils/contants";
import { buyJournalStore, podBillStore } from "@/store/order";
import { math } from "@/utils/math";
import InvoiceModal from "@/components/Modal/Invoice";
import { SvgIcon } from "@/components/SvgIcon";
import { getUser } from "@/api/config";
import { IS_MOBILE, goHref, toLogin } from "@/utils/tool";
import { InfoCircleFilled } from "@ant-design/icons";

type Props = {
  order: Order;
  form: any;
  orderBill: (InvoiceInfo & InvoiceAddress) | null;
};
const BuyJournal = (props: Props) => {
  const { t } = useTranslation();
  const user = getUser();
  // 已下单代表勾选了
  const [isChecked, setIsChecked] = useState(false);
  // 各版本定购数量（key 为版本类型 1：黑白版 2：彩色版）
  const [numbers, setNumbers] = useState<Record<number, number>>({});
  const setBuyJournal = useSetRecoilState(buyJournalStore);
  const [needInvoice, setNeedInvoice] = useState<boolean>(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [bill, setBill] = useRecoilState(podBillStore);
  // 出版社启用的 pod 版本
  const podVersions = useMemo(() => {
    return props.order.pod_versions || [];
  }, [props.order]);
  // 各版本含税单价（key 为版本类型）
  const versionPrices = useMemo(() => {
    return podVersions.reduce<Record<number, number>>((acc, version) => {
      acc[version.type] = version.price;
      return acc;
    }, {});
  }, [podVersions]);
  // 已有的 pod 订单信息（取第一个已下单的）
  const podOrder = useMemo(() => {
    return (props.order.pod_orders || []).find((item) => item.order_id);
  }, [props.order]);
  const isDisabled = useMemo(() => {
    return props.order.pay_status !== PodOrderStatus.PendingPayment;
  }, [props.order]);

  // 数量选项 0-100，0 表示不订该版本
  const numOptions = useMemo(() => {
    return Array.from({ length: 101 }, (_, i) => ({
      label: `${i}`,
      value: i,
    }));
  }, []);

  // 总金额 = Σ(版本单价 × 数量)，仅展示
  const totalAmount = useMemo(() => {
    const total = podVersions.reduce((sum, version) => {
      const num = numbers[version.type] ?? 1;
      return Number(
        math
          .add(
            math.bignumber(sum),
            math.multiply(math.bignumber(version.price), math.bignumber(num)),
          )
          .toString(),
      );
    }, 0);
    return total.toFixed(2);
  }, [podVersions, numbers]);

  /**
   * 是否购买纸质发票事件
   * @param e
   */
  const onBuyCheckedChange = (e) => {
    const newChecked = !isChecked;
    setIsChecked(newChecked);
    if (newChecked) {
      // 勾选后各版本默认数量为 1
      const nextNumbers = podVersions.reduce<Record<number, number>>(
        (acc, version) => {
          acc[version.type] = numbers[version.type] ?? 1;
          return acc;
        },
        {},
      );
      setNumbers(nextNumbers);
      setBuyJournal((prev) => ({
        ...prev,
        numbers: nextNumbers,
        prices: versionPrices,
        checked: newChecked,
        receive: {
          address: props.form.getFieldValue(["receive", "address"]) || "",
          contact: props.form.getFieldValue(["receive", "contact"]) || "",
          phone: props.form.getFieldValue(["receive", "phone"]) || "",
        },
      }));
    } else {
      setNumbers({});
      setBuyJournal((prev) => ({
        ...prev,
        numbers: {},
        checked: newChecked,
        receive: {
          address: "",
          contact: "",
          phone: "",
        },
      }));
      setBill(null);
      props.form.resetFields();
    }
  };

  // 监听表单值变化，更新 buyJournalStore
  const onFormValuesChange = (changedValues: any, allValues: any) => {
    setBuyJournal((prev) => ({
      ...prev,
      receive: {
        address: allValues.receive?.address || "",
        contact: allValues.receive?.contact || "",
        phone: allValues.receive?.phone || "",
      },
    }));
  };

  // 监听数量变化，更新本地状态和 buyJournalStore
  const onNumberChange = (type: number, value: number) => {
    const nextNumbers = { ...numbers, [type]: value };
    setNumbers(nextNumbers);
    setBuyJournal((prev) => ({
      ...prev,
      numbers: nextNumbers,
    }));
  };

  const getModalInstance = useCallback(async () => {
    if (!user) {
      toLogin();
      return;
    }
    //    sentry4Action("填写发票", {
    //      order_id: order.order_id,
    //      order_type: order.expense_type,
    //      publishing_house_name: order.publishing_house_name,
    //      journal_name: order.journal_name,
    //      article_title: order.article_title,
    //      cae: order.contact_author_email.split("@").join(" "),
    //      meeting_name: order?.meeting_name,
    //    });
    if (props.order.pay_status == PayResultStatus.UnDone) {
      setShowInvoiceModal(true);
    }
  }, [bill]);

  const TipInfo = () => {
    return (
      <Flex className="buy-journal-remark" gap={8} vertical>
        <div>
          {t(
            "1、我们采用按需印刷的方式为您提供纸本期刊服务，订单下单后除质量问题外不支持退定，如遇质量问题请与客服联系。"
          )}
        </div>
        <div>{t("2、当期期刊正式出版后45个工作日内发货。")}</div>
        <div>{t("3、您随后可在个人中心查看订单及物流信息。")}</div>
      </Flex>
    );
  };
  /**
   * 是否选择纸质发票切换事件
   * @param isNeed
   */

  const changeNeedBill = (e) => {
    const isNeed = e.target.value === "yes";
    if (isNeed && !user) {
      toLogin();
      return;
    }
    setNeedInvoice(isNeed);
    // 没有发票情况下，直接弹出
    if (isNeed && !bill) {
      setShowInvoiceModal(true);
      return;
    }
    if (!isNeed) {
      setBill(null);
    }
  };

  /**
   * 关闭发票弹出框
   */
  const closeInvoiceModal = (isCancel: boolean) => {
    setShowInvoiceModal(false);
    // 如果没有发票情况下，切换到否
    if (!bill && isCancel) {
      setNeedInvoice(false);
    }
  };

  useEffect(() => {
    const podOrders = (props.order.pod_orders || []).filter(
      (item) => item.order_id,
    );
    setIsChecked(podOrders.length > 0);
    const firstPodOrder = podOrders[0];
    if (firstPodOrder?.bill?.enable) {
      setBill(firstPodOrder.bill);
      setNeedInvoice(true);
    }
    if (podOrders.length > 0) {
      // 按版本类型回填已有 pod 订单的数量
      const backfillNumbers = podOrders.reduce<Record<number, number>>(
        (acc, item) => {
          acc[item.type] = item.number;
          return acc;
        },
        {},
      );
      setNumbers((prev) => ({ ...prev, ...backfillNumbers }));
      setBuyJournal((prev) => ({
        ...prev,
        checked: true,
        numbers: { ...prev.numbers, ...backfillNumbers },
        prices: versionPrices,
        receive: firstPodOrder.receive,
      }));
    } else {
      setBuyJournal((prev) => ({ ...prev, prices: versionPrices }));
    }
  }, [props.order.pod_orders, versionPrices]);

  /**
   * 未填写情况下，使用订单的发票信息
   */
  const defaultBill = useMemo(() => {
    if (bill) return bill;
    if (!props.orderBill) return bill;
    const { bill_remark, bill_content, ...remainOrderBill } = props.orderBill;
    return {
      ...remainOrderBill,
      bill_remark: "",
      bill_content: OrderTypeToInvoiceContentMap[SOURCE_ORDER_TYPE.Pod],
    };
  }, [bill, props.orderBill]);

  return (
    <div
      className={`buy-journal-box ${isChecked ? "buy-journal-box-active" : ""}`}
    >
      <div className="buy-journal-box-item check-box">
        <Checkbox
          checked={isChecked}
          onChange={onBuyCheckedChange}
          style={{ color: "#0271E3" }}
          disabled={isDisabled}
        >
          {t("定购当期纸本期刊")}
        </Checkbox>

        {isChecked ? (
          <Tooltip placement="top" title={<TipInfo />}>
            <InfoCircleFilled style={{ color: "#1677ff", fontSize: 20 }} />
          </Tooltip>
        ) : (
          <TipInfo />
        )}
      </div>
      {isChecked && (
        <div>
          <Form
            form={props.form}
            labelAlign="left"
            requiredMark={false}
            onValuesChange={onFormValuesChange}
            initialValues={{
              receive: podOrder?.receive,
            }}
            disabled={isDisabled}
          >
            <div className="buy-journal-box-item buy-base">
              <div className="item-title buy-info-title">{t("定购信息")}</div>
              <div>
                {podVersions.map((version) => (
                  <div
                    key={version.type}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 24,
                      marginBottom: 12,
                    }}
                  >
                    <span>
                      {t(podVersionNameMap[version.type])}-{t("单价")}：
                      {`¥${version.price.toFixed(2)}/${t("本")}`}
                    </span>
                    <span>
                      {t("数量")}：
                      <Select
                        style={{ width: 160 }}
                        options={numOptions}
                        value={numbers[version.type] ?? 1}
                        onChange={(val) => onNumberChange(version.type, val)}
                        disabled={isDisabled}
                      />
                    </span>
                  </div>
                ))}
                <div style={{ fontWeight: 600 }}>
                  {t("总金额")}： ¥{totalAmount}
                </div>
              </div>
            </div>

            <div className="buy-journal-box-item delivery-box">
              <div className="item-title delivery-title">{t("配送信息")}</div>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name={["receive", "contact"]}
                    label={t("收件人姓名")}
                    rules={[
                      {
                        required: true,
                        message: `${t("收件人姓名")}${t("不能为空")}`,
                      },
                      {
                        max: 80,
                        message: `${t("maxChart", { count: 80 })}`,
                      },
                    ]}
                  >
                    <Input
                      disabled={isDisabled}
                      placeholder={t("必填")}
                      variant="borderless"
                      className="item-input"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name={["receive", "phone"]}
                    label={t("收件人手机号")}
                    rules={[
                      {
                        required: true,
                        message: `${t("收件人手机号")}${t("不能为空")}`,
                      },
                      {
                        pattern: new RegExp(/^[1|8]\d{10}$/i),
                        message: t("输入的手机号不正确"),
                      },
                    ]}
                  >
                    <Input
                      disabled={isDisabled}
                      placeholder={t("必填")}
                      variant="borderless"
                      className="item-input"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name={["receive", "address"]}
                    label={t("收件地址")}
                    rules={[
                      {
                        required: true,
                        message: `${t("收件地址")}${t("不能为空")}`,
                      },
                      {
                        max: 200,
                        message: `${t("maxChart", { count: 200 })}`,
                      },
                    ]}
                  >
                    <Input
                      disabled={isDisabled}
                      placeholder={t("必填")}
                      variant="borderless"
                      className="item-input"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
            <div className="buy-journal-box-item need-bill-box">
              <div className="item-title bill-title">{t("是否需要发票？")}</div>
              <Radio.Group
                value={needInvoice ? "yes" : "no"}
                onChange={changeNeedBill}
                disabled={isDisabled}
              >
                <Radio style={{ marginBottom: 16 }} value="yes">
                  {t("是，需要发票（发货后随货开具）")}
                </Radio>
                <br />
                <Radio value="no">{t("否，不需要发票")}</Radio>
              </Radio.Group>

              <div style={{ color: "#999", marginTop: 8 }}>
                *
                {t(
                  "温馨提示：纸本期刊订单将在发货完成后开出发票，与APC发票分别开具"
                )}
              </div>
              {bill && (
                <div className="invoice-edit">
                  <div className="btn-line">
                    <div>{t("发票服务")}</div>
                    {!isDisabled && (
                      <div className="edit">
                        <span
                          className="btn"
                          onClick={() => getModalInstance()}
                        >
                          <SvgIcon icon="icon-tianxie" width={14} height={14} />
                          {t("修改")}
                        </span>
                        <Divider type="vertical" />
                        <span
                          className="btn"
                          onClick={() => {
                            Modal.confirm({
                              title: t("确认"),
                              content: t("是否确认删除 ?"),
                              okText: t("确认"),
                              cancelText: t("取消"),
                              onOk: () => {
                                setBill(null);
                                setNeedInvoice(false);
                              },
                            });
                          }}
                        >
                          <SvgIcon icon="icon-shanchu" width={14} height={14} />
                          {t("清除")}
                        </span>
                      </div>
                    )}
                  </div>
                  <Divider className="h-divider" />
                  <div className="text-line">
                    <div>
                      <span className="title">{t("发票类型")}</span>
                      <span className="value">
                        {t(InvoiceTypeMap[bill.bill_type])}
                        {/* {bill.bill_type === 1
                        ? t("电子增值税发票")
                        : t("增值税专用发票")} */}
                      </span>
                    </div>
                    <div>
                      <span className="title">{t("抬头名称")}</span>
                      <span className="value">{bill.title_title}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Form>
          {showInvoiceModal && (
            <InvoiceModal
              hiddenModal={closeInvoiceModal}
              invoiceInfo={defaultBill}
              getInvoiceInfo={(info) => {
                // let filteredObj = { ...bill, ...info };
                // if (filteredObj.bill_type === 1) {
                //   filteredObj = Object.keys(filteredObj).reduce(
                //     (acc, key) => {
                //       if (!key.includes("address") || key === "title_address") {
                //         acc[key] = filteredObj[key];
                //       }
                //       return acc;
                //     },
                //     {} as InvoiceInfo & InvoiceAddress
                //   );
                // }
                setBill(info);
              }}
              orderIds={[props.order.order_id]}
              additionalParams={{
                orderType: SOURCE_ORDER_TYPE.Pod,
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default BuyJournal;
